import { useCallback, useMemo, useRef, useState } from 'react';

import { useModal } from '../use-modal';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { CarrierConnectionFlow, Workflows } from '../../tracking/tracking-constants';
import { FreightAccountActivationModal } from '../../components/modals/freight-account-activation-modal';
import {
  ConnectFreightProviderModalContextProvider,
  useConnectFreightProviderContext,
} from '../../components/modals/freight-provider-registration-modal/context-provider';
import { WwexRegistrationContainer } from '../../pages/ltl-freight/components/freight-quote-modal/components/wwex-registration-container';
import { UNISHIPPERS_API_CODE, WWEX_API_CODE } from '../../pages/settings/carriers/freight/constants';

type FreightAccountActivationModalsProps = {
  closeChooser: () => void;
  closeWwexRegistration: () => void;
  handleActivate: () => void;
  handleCloseChooser: () => void;
  isChooserOpen: boolean;
  isWwexRegistrationOpen: boolean;
  onManageClick?: () => Promise<boolean>;
  onProviderConnected: (sellerFreightProviderId: number | null) => void;
  shouldShowActivateOption: boolean;
};

const FreightAccountActivationModals = ({
  closeChooser,
  closeWwexRegistration,
  handleActivate,
  handleCloseChooser,
  isChooserOpen,
  isWwexRegistrationOpen,
  onManageClick,
  onProviderConnected,
  shouldShowActivateOption,
}: FreightAccountActivationModalsProps) => {
  const { onConnectFreightProvider } = useConnectFreightProviderContext();
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);

  const handleConnectProvider = useCallback(
    (apiCode: string) => {
      closeChooser();
      trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_FLOW_CHOSEN, {
        flow: CarrierConnectionFlow.CONNECT,
        provider: apiCode,
        is_freight: true,
      });
      onConnectFreightProvider(apiCode);
    },
    [closeChooser, onConnectFreightProvider, trackCarrierConnectionStep],
  );

  const handleConnectWwex = useCallback(() => handleConnectProvider(WWEX_API_CODE), [handleConnectProvider]);

  const handleConnectUnishippers = useCallback(() => handleConnectProvider(UNISHIPPERS_API_CODE), [handleConnectProvider]);

  const handleWwexRegistrationSuccess = useCallback(
    (sellerFreightProviderId: number | null) => {
      onProviderConnected(sellerFreightProviderId);
      closeWwexRegistration();
    },
    [onProviderConnected, closeWwexRegistration],
  );

  return (
    <>
      {isChooserOpen && (
        <FreightAccountActivationModal
          onActivate={handleActivate}
          onClose={handleCloseChooser}
          onConnectUnishippers={handleConnectUnishippers}
          onConnectWwex={handleConnectWwex}
          shouldShowActivateOption={shouldShowActivateOption}
        />
      )}
      <WwexRegistrationContainer
        isOpen={isWwexRegistrationOpen}
        onClose={closeWwexRegistration}
        onManageClick={onManageClick}
        onSuccess={handleWwexRegistrationSuccess}
      />
    </>
  );
};

export const useFreightAccountActivationFlow = (
  source: string,
  onProviderConnected?: (sellerFreightProviderId: number | null) => void,
  onManageClick?: () => Promise<boolean>,
) => {
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);
  const justConnectedProviderRef = useRef(false);

  const { open: openChooserModal, close: closeChooser, isOpen: isChooserOpen } = useModal();
  const { open: openWwexRegistration, close: closeWwexRegistration, isOpen: isWwexRegistrationOpen } = useModal();

  const [shouldShowActivateOption, setShouldShowActivateOption] = useState(true);
  const [activeSource, setActiveSource] = useState(source);

  const openChooserWithActivateOption = useCallback(
    (shouldShowActivate: boolean, nextSource: string) => {
      setShouldShowActivateOption(shouldShowActivate);
      setActiveSource(nextSource);
      openChooserModal();
      trackCarrierConnectionStep(WorkflowSteps.START, {
        source: nextSource,
        flow: shouldShowActivate ? CarrierConnectionFlow.UNDECIDED : CarrierConnectionFlow.CONNECT,
        entered_from_chooser: true,
        is_freight: true,
      });
      trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_CHOOSER_SHOWN);
    },
    [openChooserModal, trackCarrierConnectionStep],
  );

  const openChooser = useCallback(() => openChooserWithActivateOption(true, source), [openChooserWithActivateOption, source]);

  const openConnectOnlyChooser = useCallback(() => openChooserWithActivateOption(false, source), [openChooserWithActivateOption, source]);

  const handleCloseChooser = useCallback(() => {
    closeChooser();
    trackCarrierConnectionStep(WorkflowSteps.CANCEL, { source: activeSource, is_freight: true });
  }, [closeChooser, trackCarrierConnectionStep, activeSource]);

  const handleActivate = useCallback(() => {
    closeChooser();
    trackCarrierConnectionStep(WorkflowSteps.CARRIER_CONNECTION_FLOW_CHOSEN, {
      flow: CarrierConnectionFlow.REGISTER,
      provider: WWEX_API_CODE,
      is_freight: true,
    });
    openWwexRegistration();
  }, [closeChooser, openWwexRegistration, trackCarrierConnectionStep]);

  // CUI's registration form has no provider list to fall back to on its own "Back" link, so
  // ss-ui-client has to reopen the chooser it closed when the registration modal was opened.
  const handleRegistrationBack = useCallback(() => {
    openChooserModal();
  }, [openChooserModal]);

  const handleProviderConnected = useCallback(
    (sellerFreightProviderId: number | null = null) => {
      justConnectedProviderRef.current = true;
      onProviderConnected?.(sellerFreightProviderId);
    },
    [onProviderConnected],
  );

  // Bridges the gap between a successful connect and the async Redux refresh
  // that follows it, so a booking action doesn't re-prompt registration
  // while the seller's connected providers haven't caught up yet.
  const hasJustConnectedProvider = useCallback(() => justConnectedProviderRef.current, []);

  const freightAccountActivationModals = useMemo(
    () => (
      <ConnectFreightProviderModalContextProvider
        onRegistrationBack={handleRegistrationBack}
        onRegistrationSuccess={handleProviderConnected}
      >
        <FreightAccountActivationModals
          closeChooser={closeChooser}
          closeWwexRegistration={closeWwexRegistration}
          handleActivate={handleActivate}
          handleCloseChooser={handleCloseChooser}
          isChooserOpen={isChooserOpen}
          isWwexRegistrationOpen={isWwexRegistrationOpen}
          onManageClick={onManageClick}
          onProviderConnected={handleProviderConnected}
          shouldShowActivateOption={shouldShowActivateOption}
        />
      </ConnectFreightProviderModalContextProvider>
    ),
    [
      closeChooser,
      closeWwexRegistration,
      handleActivate,
      handleCloseChooser,
      handleRegistrationBack,
      isChooserOpen,
      isWwexRegistrationOpen,
      onManageClick,
      handleProviderConnected,
      shouldShowActivateOption,
    ],
  );

  return { openChooser, openConnectOnlyChooser, freightAccountActivationModals, hasJustConnectedProvider };
};
